import React, { useState, useEffect } from 'react';
import { 
  Plug, 
  Plus, 
  Settings, 
  Trash2, 
  CheckCircle, 
  XCircle,
  RefreshCw,
  Play,
  Eye,
  EyeOff,
  Download,
  Upload,
  TestTube,
  Loader
} from 'lucide-react';

interface AdapterDefinition {
  type: string;
  name: string;
  description: string;
  icon: string;
  requiredCredentials: string[];
  optionalCredentials?: string[];
  capabilities: string[];
}

interface AdapterConfig {
  name: string;
  type: string;
  enabled: boolean;
  connected?: boolean;
  credentials: Record<string, string>;
  endpoints?: Record<string, string>;
}

interface RegisteredAdapter {
  id: string;
  name: string;
  type: string;
  enabled: boolean;
  connected: boolean;
  requestsInWindow: number;
  cacheSize: number;
  config?: AdapterConfig;
}

const AdapterManager: React.FC = () => {
  const [adapters, setAdapters] = useState<RegisteredAdapter[]>([]);
  const [definitions, setDefinitions] = useState<AdapterDefinition[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedType, setSelectedType] = useState<string>('');
  const [newAdapterId, setNewAdapterId] = useState('');
  const [credentials, setCredentials] = useState<Record<string, string>>({});
  const [showCredentials, setShowCredentials] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(false);
  const [testResults, setTestResults] = useState<Record<string, boolean>>({});

  const API_BASE = 'https://biig-api.b-9f2.workers.dev';

  useEffect(() => {
    loadAdapters();
    loadDefinitions();
  }, []);

  const loadAdapters = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/adapters`);
      const data = await response.json();
      setAdapters(data.adapters || []);
    } catch (error) {
      console.error('Failed to load adapters:', error);
    }
  };

  const loadDefinitions = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/adapters/definitions`);
      const data = await response.json();
      setDefinitions(data.definitions || []);
    } catch (error) {
      console.error('Failed to load definitions:', error);
    }
  };

  const registerAdapter = async () => {
    if (!selectedType || !newAdapterId) return;

    const definition = definitions.find(d => d.type === selectedType);
    if (!definition) return;

    setLoading(true);
    try {
      const config: AdapterConfig = {
        name: definition.name,
        type: definition.type,
        enabled: true,
        credentials
      };

      const response = await fetch(`${API_BASE}/api/adapters`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: newAdapterId, config })
      });

      const data = await response.json();
      
      if (data.success) {
        await loadAdapters();
        setShowAddModal(false);
        resetForm();
      } else {
        console.error('Registration failed:', data.error);
      }
    } catch (error) {
      console.error('Failed to register adapter:', error);
    } finally {
      setLoading(false);
    }
  };

  const testAdapter = async (id: string) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/adapters/${id}/test`, {
        method: 'POST'
      });
      const data = await response.json();
      setTestResults({ ...testResults, [id]: data.success });
      setTimeout(() => {
        setTestResults(prev => {
          const updated = { ...prev };
          delete updated[id];
          return updated;
        });
      }, 3000);
    } catch (error) {
      console.error('Test failed:', error);
      setTestResults({ ...testResults, [id]: false });
    } finally {
      setLoading(false);
    }
  };

  const removeAdapter = async (id: string) => {
    if (!confirm(`Remove adapter ${id}?`)) return;

    try {
      const response = await fetch(`${API_BASE}/api/adapters/${id}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        await loadAdapters();
      }
    } catch (error) {
      console.error('Failed to remove adapter:', error);
    }
  };

  const executeMethod = async (id: string, method: string, params?: any[]) => {
    try {
      const response = await fetch(`${API_BASE}/api/adapters/${id}/execute`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ method, params })
      });
      
      const data = await response.json();
      console.log(`${id}.${method} result:`, data);
      return data;
    } catch (error) {
      console.error('Execution failed:', error);
    }
  };

  const exportConfigs = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/adapters/export`);
      const data = await response.json();
      
      const blob = new Blob([JSON.stringify(data.configs, null, 2)], {
        type: 'application/json'
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'adapter-configs.json';
      a.click();
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  const resetForm = () => {
    setNewAdapterId('');
    setSelectedType('');
    setCredentials({});
  };

  const getStatusColor = (adapter: RegisteredAdapter) => {
    if (!adapter.enabled) return 'bg-gray-500';
    if (adapter.connected) return 'bg-green-500';
    return 'bg-yellow-500';
  };

  const getStatusText = (adapter: RegisteredAdapter) => {
    if (!adapter.enabled) return 'Disabled';
    if (adapter.connected) return 'Connected';
    return 'Disconnected';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 p-8">
      <div className="max-w-7xl mx-auto">
        
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2 flex items-center gap-3">
            <Plug className="w-10 h-10 text-blue-400" />
            API Adapter Manager
          </h1>
          <p className="text-gray-300">
            Connect any API to your pousada system
          </p>
        </div>

        {/* Actions Bar */}
        <div className="bg-gray-800 rounded-xl p-4 mb-6 flex items-center justify-between">
          <div className="flex gap-3">
            <button
              onClick={() => setShowAddModal(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Adapter
            </button>
            <button
              onClick={exportConfigs}
              className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Export
            </button>
          </div>
          <button
            onClick={loadAdapters}
            className="p-2 text-gray-400 hover:text-white transition-colors"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>

        {/* Adapters Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          {adapters.map(adapter => (
            <div key={adapter.id} className="bg-gray-800 rounded-xl p-5">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className={`w-3 h-3 rounded-full ${getStatusColor(adapter)}`} />
                  <div>
                    <h3 className="text-lg font-semibold text-white">{adapter.name}</h3>
                    <p className="text-xs text-gray-400">{adapter.id}</p>
                  </div>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full ${
                  adapter.connected ? 'bg-green-900 text-green-300' : 'bg-gray-700 text-gray-400'
                }`}>
                  {getStatusText(adapter)}
                </span>
              </div>

              <div className="space-y-2 mb-4 text-sm">
                <div className="flex justify-between text-gray-400">
                  <span>Type:</span>
                  <span className="text-white">{adapter.type}</span>
                </div>
                <div className="flex justify-between text-gray-400">
                  <span>Requests:</span>
                  <span className="text-white">{adapter.requestsInWindow}/min</span>
                </div>
                <div className="flex justify-between text-gray-400">
                  <span>Cache:</span>
                  <span className="text-white">{adapter.cacheSize} items</span>
                </div>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => testAdapter(adapter.id)}
                  className="flex-1 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-1"
                  disabled={loading}
                >
                  {testResults[adapter.id] === true ? (
                    <CheckCircle className="w-4 h-4" />
                  ) : testResults[adapter.id] === false ? (
                    <XCircle className="w-4 h-4" />
                  ) : (
                    <TestTube className="w-4 h-4" />
                  )}
                  Test
                </button>
                <button
                  className="p-1.5 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition-colors"
                  title="Settings"
                >
                  <Settings className="w-4 h-4" />
                </button>
                <button
                  onClick={() => removeAdapter(adapter.id)}
                  className="p-1.5 bg-gray-700 text-gray-300 rounded-lg hover:bg-red-600 hover:text-white transition-colors"
                  title="Remove"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Available Definitions */}
        <div className="bg-gray-800 rounded-xl p-6">
          <h2 className="text-xl font-semibold text-white mb-4">Available Integrations</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {definitions.map(def => (
              <div key={def.type} className="bg-gray-700 rounded-lg p-4">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-2xl">{def.icon}</span>
                  <div>
                    <h3 className="font-semibold text-white">{def.name}</h3>
                    <p className="text-xs text-gray-400">{def.type}</p>
                  </div>
                </div>
                <p className="text-sm text-gray-300 mb-3">{def.description}</p>
                <div className="flex flex-wrap gap-1">
                  {def.capabilities.slice(0, 3).map(cap => (
                    <span key={cap} className="text-xs bg-gray-800 text-gray-300 px-2 py-1 rounded">
                      {cap}
                    </span>
                  ))}
                  {def.capabilities.length > 3 && (
                    <span className="text-xs text-gray-500">+{def.capabilities.length - 3}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Add Adapter Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-gray-800 rounded-xl p-6 w-full max-w-md">
              <h2 className="text-2xl font-bold text-white mb-4">Add New Adapter</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Adapter Type</label>
                  <select
                    value={selectedType}
                    onChange={(e) => {
                      setSelectedType(e.target.value);
                      const def = definitions.find(d => d.type === e.target.value);
                      if (def) {
                        const creds: Record<string, string> = {};
                        def.requiredCredentials.forEach(c => creds[c] = '');
                        setCredentials(creds);
                      }
                    }}
                    className="w-full bg-gray-700 text-white px-3 py-2 rounded-lg"
                  >
                    <option value="">Select type...</option>
                    {definitions.map(def => (
                      <option key={def.type} value={def.type}>
                        {def.icon} {def.name}
                      </option>
                    ))}
                  </select>
                </div>

                {selectedType && (
                  <>
                    <div>
                      <label className="block text-sm text-gray-400 mb-2">Adapter ID</label>
                      <input
                        type="text"
                        value={newAdapterId}
                        onChange={(e) => setNewAdapterId(e.target.value)}
                        placeholder="e.g., booking-main"
                        className="w-full bg-gray-700 text-white px-3 py-2 rounded-lg"
                      />
                    </div>

                    {definitions
                      .find(d => d.type === selectedType)
                      ?.requiredCredentials.map(cred => (
                        <div key={cred}>
                          <label className="block text-sm text-gray-400 mb-2">
                            {cred} <span className="text-red-400">*</span>
                          </label>
                          <div className="relative">
                            <input
                              type={showCredentials[cred] ? 'text' : 'password'}
                              value={credentials[cred] || ''}
                              onChange={(e) => setCredentials({
                                ...credentials,
                                [cred]: e.target.value
                              })}
                              className="w-full bg-gray-700 text-white px-3 py-2 pr-10 rounded-lg"
                            />
                            <button
                              type="button"
                              onClick={() => setShowCredentials({
                                ...showCredentials,
                                [cred]: !showCredentials[cred]
                              })}
                              className="absolute right-2 top-2.5 text-gray-400 hover:text-white"
                            >
                              {showCredentials[cred] ? (
                                <EyeOff className="w-4 h-4" />
                              ) : (
                                <Eye className="w-4 h-4" />
                              )}
                            </button>
                          </div>
                        </div>
                      ))}
                  </>
                )}
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={registerAdapter}
                  disabled={loading || !selectedType || !newAdapterId}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <Loader className="w-4 h-4 animate-spin" />
                  ) : (
                    <Plus className="w-4 h-4" />
                  )}
                  Register
                </button>
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    resetForm();
                  }}
                  className="flex-1 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default AdapterManager;